import React, {useState, useEffect} from "react"
import Mustache from "mustache"
import Editor from "@monaco-editor/react"
import Button from "../Button/Button"
import Icon from "../Icon/Icon"
import {getNestedValue, setNestedValue} from "../../utilities/objects"
import classNames from "classnames"
import {
	ArrayVariable,
	extractMustacheVariables,
	getFieldsAddableToList,
	getMustacheMeta,
	moveFieldIntoList,
	omitRootCopiesOfListItemFields,
	withMustacheMeta,
} from "./mustacheVariables"
import {containsScriptTags, isValidJson, SECURITY_WARNING} from "./editorValidation"
import {createSampleValueResolver, UI_COLORS} from "./sampleValues"

interface MustacheEditorProps {
	initialHtml: string
	onSave: (html: string, subject?: string, samples?: string) => void
	defaultVariables?: Record<string, any>
	initialSubject?: string
	showSubjectField?: boolean
	samples?: string
}

type ViewMode = "preview-only" | "side-by-side" | "edit-only"

export default function MustacheEditor({
	initialHtml,
	onSave,
	defaultVariables = {},
	initialSubject = "",
	showSubjectField = false,
	samples,
}: MustacheEditorProps) {
	const [initialized, setInitialized] = useState(false)
	const [htmlContent, setHtmlContent] = useState(initialHtml)
	const [variablesObject, setVariablesObject] = useState<Record<string, any>>(defaultVariables)
	const [renderedHtml, setRenderedHtml] = useState("")
	const [previewError, setPreviewError] = useState<string | null>(null)
	const [viewMode, setViewMode] = useState<ViewMode>("side-by-side")
	const [showRawJson, setShowRawJson] = useState(false)
	const [variablesJson, setVariablesJson] = useState(JSON.stringify(defaultVariables, null, 2))
	const [hasScriptTags, setHasScriptTags] = useState(false)
	const [subject, setSubject] = useState(initialSubject)
	const [arrayVariables, setArrayVariables] = useState<ArrayVariable[]>([])
	const [detectedVariables, setDetectedVariables] = useState<string[]>([])
	const [conditionalVariables, setConditionalVariables] = useState<string[]>([])
	const [flattenedVariables, setFlattenedVariables] = useState<Record<string, string>>({})
	const [arrayAsBooleanMap, setArrayAsBooleanMap] = useState<Record<string, boolean>>({})

	const extractVariables = (html: string, existingVariables: Record<string, any> = variablesObject) =>
		extractMustacheVariables(html, {
			existingVariables,
			getSampleValue: getSmartSampleValue,
		})

	// Extract variables and update state in an effect when content changes
	useEffect(() => {
		let variables: string[] = []
		let arrays: ArrayVariable[] = []
		let conditionals: string[] = []
		try {
			const result = extractVariables(htmlContent)
			variables = result.variables
			arrays = result.arrays
			conditionals = result.conditionals
		} catch (e) {
			console.error("Error extracting variables:", e)
			return
		}

		// Debug logging to see how sections are classified
		console.log("Detected variables:", variables)
		console.log(
			"Detected arrays:",
			arrays.map((a) => a.name)
		)
		console.log("Detected conditionals:", conditionals)

		// Make sure array rows use the current variablesObject data
		const updatedArrays = arrays.map((arrayVar) => {
			// If we already have data for this array in variablesObject, use it
			const existingArray = variablesObject[arrayVar.name]
			if (existingArray && Array.isArray(existingArray) && existingArray.length > 0) {
				return {
					...arrayVar,
					rows: existingArray,
				}
			}
			return arrayVar
		})

		setDetectedVariables(variables)
		setFlattenedVariables(flattenVariables(variables))
		setArrayVariables(updatedArrays)
		setConditionalVariables(conditionals)

		const stripped = omitRootCopiesOfListItemFields(variablesObject, updatedArrays)
		if (JSON.stringify(stripped) !== JSON.stringify(variablesObject)) {
			setVariablesObject(stripped)
		}
	}, [htmlContent, variablesObject])

	const getSmartSampleValue = createSampleValueResolver({samples})

	// Create a flattened object from the variable paths
	const flattenVariables = (variables: string[]) => {
		const result: Record<string, string> = {}

		variables.forEach((variable) => {
			// Check if value already exists in variables object
			const existingValue = getNestedValue(variablesObject, variable)

			if (existingValue !== undefined) {
				result[variable] = existingValue
			} else {
				result[variable] = getSmartSampleValue(variable)
			}
		})

		return result
	}

	const createArrayDisplayMode = (arrayAsBooleanMap: Record<string, boolean>) =>
		Object.keys(arrayAsBooleanMap)
			.filter((key) => arrayAsBooleanMap[key])
			.reduce((obj, key) => {
				obj[key] = "boolean"
				return obj
			}, {} as Record<string, string>)

	const buildVariablesWithMeta = (variables: Record<string, any> = variablesObject) => {
		const meta = getMustacheMeta(variables)
		return withMustacheMeta(variables, {
			...meta,
			arrayDisplayMode: createArrayDisplayMode(arrayAsBooleanMap),
		})
	}

	// Keep JSON in sync with the form. Do not parse JSON back into state here —
	// that fights the JSON editor and restores top-level copies of list item fields.
	useEffect(() => {
		if (showRawJson) return

		setVariablesJson(JSON.stringify(buildVariablesWithMeta(), null, 2))
	}, [variablesObject, showRawJson, arrayAsBooleanMap, arrayVariables])

	// Initialize variables with sample values on first render only
	useEffect(() => {
		// Start with default variables, then overlay with samples if available
		const initialVariablesObject = {...defaultVariables}

		try {
			if (samples) {
				const parsedSamples = JSON.parse(samples)
				if (Array.isArray(parsedSamples) && parsedSamples.length > 0) {
					Object.assign(initialVariablesObject, parsedSamples[0])
				}
			}
		} catch (e) {
			console.warn("Failed to parse samples JSON during initialization:", e)
		}

		const initialArrayAsBooleanMap: Record<string, boolean> = {}
		const metaSource = getMustacheMeta(initialVariablesObject)
		if (metaSource.arrayDisplayMode) {
			Object.entries(metaSource.arrayDisplayMode).forEach(([key, mode]) => {
				if (mode === "boolean") {
					initialArrayAsBooleanMap[key] = true
				}
			})
		}

		const {
			variables: initialVariables,
			arrays: initialArrays,
			conditionals: initialConditionals,
		} = extractVariables(initialHtml, initialVariablesObject)

		console.log("Initializing variables:", initialVariables)
		console.log("Initializing conditionals:", initialConditionals)

		// Initialize regular variables
		initialVariables.forEach((variable) => {
			if (getNestedValue(initialVariablesObject, variable) === undefined) {
				Object.assign(
					initialVariablesObject,
					setNestedValue(initialVariablesObject, variable, getSmartSampleValue(variable))
				)
			}
		})

		// Initialize conditional variables with smart sample values instead of false by default
		initialConditionals.forEach((conditional) => {
			if (getNestedValue(initialVariablesObject, conditional) === undefined) {
				Object.assign(
					initialVariablesObject,
					setNestedValue(initialVariablesObject, conditional, getSmartSampleValue(conditional))
				)
			}
		})

		// Initialize array variables with sample data
		initialArrays.forEach((arrayVar) => {
			// Check if this array should be displayed as a boolean based on metadata
			if (initialArrayAsBooleanMap[arrayVar.name]) {
				// If it's marked as boolean but doesn't have a boolean value, initialize it
				if (typeof initialVariablesObject[arrayVar.name] !== "boolean") {
					initialVariablesObject[arrayVar.name] = true // Default to true for better preview
				}
			}
			// Check if the existing value is a boolean - if so, initialize it as a boolean display
			else if (typeof initialVariablesObject[arrayVar.name] === "boolean") {
				initialArrayAsBooleanMap[arrayVar.name] = true
			} else if (!initialVariablesObject[arrayVar.name] || !Array.isArray(initialVariablesObject[arrayVar.name])) {
				initialVariablesObject[arrayVar.name] = arrayVar.rows
			}
		})

		const reconciledVariables = omitRootCopiesOfListItemFields(initialVariablesObject, initialArrays)
		const extracted = extractVariables(initialHtml, reconciledVariables)

		setVariablesObject(reconciledVariables)
		setVariablesJson(JSON.stringify(buildVariablesWithMeta(reconciledVariables), null, 2))
		setDetectedVariables(extracted.variables)
		setFlattenedVariables(flattenVariables(extracted.variables))
		setArrayVariables(extracted.arrays)
		setConditionalVariables(extracted.conditionals)
		setArrayAsBooleanMap(initialArrayAsBooleanMap)

		setHtmlContent(initialHtml)
		setInitialized(true)
	}, [])

	// Auto-extract variables when HTML content changes
	useEffect(() => {
		if (!initialized) return

		// We already extract and process variables in another effect,
		// so we just need to look for new ones here
		const updatedVariables = {...variablesObject}
		let hasNewVariables = false

		// Check if there are any new variables that don't exist yet
		detectedVariables.forEach((variable) => {
			if (getNestedValue(updatedVariables, variable) === undefined) {
				// Only add new variables that don't exist yet
				const newObj = setNestedValue(updatedVariables, variable, getSmartSampleValue(variable))
				Object.assign(updatedVariables, newObj)
				hasNewVariables = true
			}
		})

		// Check for new conditional variables - use smart sample values instead of false
		conditionalVariables.forEach((conditional) => {
			if (getNestedValue(updatedVariables, conditional) === undefined) {
				const newObj = setNestedValue(updatedVariables, conditional, getSmartSampleValue(conditional))
				Object.assign(updatedVariables, newObj)
				hasNewVariables = true
			}
		})

		// Only update if we found new variables
		if (hasNewVariables) {
			setVariablesObject(updatedVariables)
		}
	}, [initialized, detectedVariables, conditionalVariables])

	// Render template when initialized or variables change
	useEffect(() => {
		if (!initialized) return

		// Check if content has script tags
		if (hasScriptTags) {
			setPreviewError(SECURITY_WARNING)
			setRenderedHtml(
				`<div style="padding: 20px; color: ${UI_COLORS.error};">Preview blocked due to security concerns. Please remove all script tags.</div>`
			)
			return
		}

		try {
			// Create a processed version of variables for rendering
			const processedVariables = {...variablesObject}

			// Process array variables that are now booleans
			Object.keys(arrayAsBooleanMap).forEach((arrayName) => {
				if (arrayAsBooleanMap[arrayName]) {
					// If an array is being handled as a boolean, ensure it has the right data type
					processedVariables[arrayName] = Boolean(processedVariables[arrayName])
				}
			})

			// Render the template with processed variables
			const rendered = Mustache.render(htmlContent, processedVariables)

			// Double-check the rendered output for script tags that might have been inserted through variables
			if (containsScriptTags(rendered)) {
				setPreviewError(SECURITY_WARNING)
				setRenderedHtml(
					`<div style="padding: 20px; color: ${UI_COLORS.error};">Preview blocked due to security concerns. Please check your variables for script content.</div>`
				)
				return
			}

			setRenderedHtml(rendered)
			setPreviewError(null)
		} catch (error) {
			console.error("Render failed", error)
			setPreviewError(`Mustache rendering error: ${error instanceof Error ? error.message : String(error)}`)
		}
	}, [initialized, variablesObject, htmlContent, hasScriptTags, arrayAsBooleanMap])

	const handleHtmlChange = (value: string | undefined) => {
		const newContent = value || ""
		setHtmlContent(newContent)

		// Check for script tags whenever HTML content changes
		const isValid = !containsScriptTags(newContent)
		setHasScriptTags(!isValid)

		// If script tags are detected, set an error
		if (!isValid) {
			setPreviewError(SECURITY_WARNING)
		} else if (previewError === SECURITY_WARNING) {
			// Only clear the error if it was a script tag error
			setPreviewError(null)
		}
	}

	const handleVariableChange = (variable: string, value: string) => {
		const updatedVariables = setNestedValue({...variablesObject}, variable, value)
		setVariablesObject(updatedVariables)
	}

	const handleRawJsonChange = (value: string | undefined) => {
		if (isValidJson(value || "")) {
			const parsed = JSON.parse(value || "{}")
			const extracted = extractVariables(htmlContent, parsed)
			const reconciled = omitRootCopiesOfListItemFields(parsed, extracted.arrays)
			const nextArrayAsBooleanMap = {...arrayAsBooleanMap}
			extracted.arrays.forEach((arrayVar) => {
				const current = reconciled[arrayVar.name]
				if (typeof current === "boolean") {
					nextArrayAsBooleanMap[arrayVar.name] = true
				} else if (Array.isArray(current) && nextArrayAsBooleanMap[arrayVar.name]) {
					nextArrayAsBooleanMap[arrayVar.name] = false
				}
			})
			setArrayAsBooleanMap(nextArrayAsBooleanMap)
			setArrayVariables(extracted.arrays)
			setConditionalVariables(extracted.conditionals)
			setDetectedVariables(extracted.variables)
			setFlattenedVariables(flattenVariables(extracted.variables))
			setVariablesObject(reconciled)
			setVariablesJson(JSON.stringify(reconciled, null, 2))
			if (previewError === "Invalid JSON") {
				setPreviewError(null)
			}
		} else {
			setPreviewError("Invalid JSON")
		}
	}

	const handleSave = () => {
		// Prevent saving if HTML contains script tags
		if (hasScriptTags) {
			setPreviewError("Cannot save template with <script> tags. Please remove them first.")
			return
		}

		const cleanVariables = buildVariablesWithMeta(omitRootCopiesOfListItemFields(variablesObject, arrayVariables))

		// Create samples array from current template variables (single item array)
		const currentSamples = [cleanVariables]

		// Convert to JSON string
		const samplesJson = JSON.stringify(currentSamples)

		// Update the stored JSON with the clean version including metadata
		setVariablesJson(JSON.stringify(cleanVariables, null, 2))

		if (showSubjectField) {
			onSave(htmlContent, subject, samplesJson)
		} else {
			onSave(htmlContent, undefined, samplesJson)
		}
	}

	const toggleRawJson = () => {
		setShowRawJson(!showRawJson)
	}

	// Check initial HTML for script tags when component mounts
	useEffect(() => {
		const isValid = !containsScriptTags(initialHtml)
		setHasScriptTags(!isValid)

		if (!isValid) {
			setPreviewError(SECURITY_WARNING)
		}
	}, [initialHtml])

	const handleArrayRowChange = (arrayName: string, rowIndex: number, property: string, value: string | boolean) => {
		const updatedArrays = arrayVariables.map((arr) => {
			if (arr.name === arrayName) {
				const newRows = [...arr.rows]
				newRows[rowIndex] = {...newRows[rowIndex], [property]: value}
				return {...arr, rows: newRows}
			}
			return arr
		})
		setArrayVariables(updatedArrays)

		// Update the main variables object
		const updatedVariables = {...variablesObject}
		updatedVariables[arrayName] = updatedArrays.find((arr) => arr.name === arrayName)?.rows || []
		setVariablesObject(updatedVariables)
	}

	const handleAddRow = (arrayName: string) => {
		const updatedArrays = arrayVariables.map((arr) => {
			if (arr.name === arrayName) {
				const newRow = arr.properties.reduce((acc, prop) => {
					acc[prop] = arr.booleanProperties.includes(prop) ? false : ""
					return acc
				}, {} as Record<string, unknown>)
				return {...arr, rows: [...arr.rows, newRow]}
			}
			return arr
		})
		setArrayVariables(updatedArrays)

		// Update the main variables object
		const updatedVariables = {...variablesObject}
		updatedVariables[arrayName] = updatedArrays.find((arr) => arr.name === arrayName)?.rows || []
		setVariablesObject(updatedVariables)
	}

	const handleRemoveRow = (arrayName: string, rowIndex: number) => {
		const updatedArrays = arrayVariables.map((arr) => {
			if (arr.name === arrayName) {
				const newRows = arr.rows.filter((_, index) => index !== rowIndex)
				return {...arr, rows: newRows.length > 0 ? newRows : [{}]} // Keep at least one empty row
			}
			return arr
		})
		setArrayVariables(updatedArrays)

		// Update the main variables object
		const updatedVariables = {...variablesObject}
		updatedVariables[arrayName] = updatedArrays.find((arr) => arr.name === arrayName)?.rows || []
		setVariablesObject(updatedVariables)
	}

	// Toggle between array and boolean representation
	const toggleArrayAsBoolean = (arrayName: string) => {
		const newArrayAsBooleanMap = {...arrayAsBooleanMap}
		newArrayAsBooleanMap[arrayName] = !arrayAsBooleanMap[arrayName]
		setArrayAsBooleanMap(newArrayAsBooleanMap)

		// Update variable value accordingly
		const updatedVariables = {...variablesObject}

		if (newArrayAsBooleanMap[arrayName]) {
			// When converting to boolean, set to true by default
			updatedVariables[arrayName] = true
		} else {
			// When converting back to array, restore array structure
			const arrayVar = arrayVariables.find((arr) => arr.name === arrayName)
			if (arrayVar) {
				updatedVariables[arrayName] = arrayVar.rows.length > 0 ? arrayVar.rows : [{}]
			}
		}

		setVariablesObject(updatedVariables)
	}

	// Handle boolean change for array variables treated as booleans
	const handleBooleanChange = (arrayName: string, value: boolean) => {
		const updatedVariables = {...variablesObject}
		updatedVariables[arrayName] = value
		setVariablesObject(updatedVariables)
	}

	const handleMoveFieldToList = (fieldName: string, listName: string) => {
		const moved = moveFieldIntoList(variablesObject, arrayVariables, fieldName, listName)
		setVariablesObject(moved.variables)
		setArrayVariables(moved.arrays)
		setConditionalVariables((current) => current.filter((name) => name !== fieldName))
	}

	const renderArrayTable = (arrayVar: ArrayVariable) => {
		// Check if this array should be rendered as a boolean
		if (arrayAsBooleanMap[arrayVar.name]) {
			return (
				<div key={arrayVar.name} className="array-variable-boolean">
					<div className="array-variable-header">
						<h4>{arrayVar.name}</h4>
						<div className="array-controls">
							<label className="toggle-display-mode">
								<span>Display as: Conditional</span>
								<Button onClick={() => toggleArrayAsBoolean(arrayVar.name)} theme="neutral" size="small">
									Switch to List
								</Button>
							</label>
						</div>
					</div>
					<div className="boolean-control">
						<label className="checkbox-container">
							<input
								type="checkbox"
								checked={Boolean(variablesObject[arrayVar.name])}
								onChange={(e) => handleBooleanChange(arrayVar.name, e.target.checked)}
							/>
							<span>Enable {arrayVar.name}</span>
						</label>
						<div className="boolean-description">
							<small>When checked, this section will be displayed in the template.</small>
						</div>
					</div>
				</div>
			)
		}

		// Original array table rendering
		return (
			<div key={arrayVar.name} className="array-variable-table">
				<div className="array-variable-header">
					<h4>{arrayVar.name}</h4>
					<div className="array-controls">
						<label className="toggle-display-mode">
							<span>Display as: List</span>
							<Button onClick={() => toggleArrayAsBoolean(arrayVar.name)} theme="neutral" size="small">
								Switch to Conditional
							</Button>
						</label>
						<Button onClick={() => handleAddRow(arrayVar.name)} theme="success" size="small">
							<Icon icon="alert-circle--interface-essential" size={12} /> Add Row
						</Button>
						{getFieldsAddableToList(arrayVariables, conditionalVariables, arrayVar.name).length > 0 && (
							<select
								className="add-list-field-select"
								value=""
								onChange={(e) => {
									if (e.target.value) handleMoveFieldToList(e.target.value, arrayVar.name)
								}}
							>
								<option value="">Add field to list…</option>
								{getFieldsAddableToList(arrayVariables, conditionalVariables, arrayVar.name).map((fieldName) => (
									<option key={fieldName} value={fieldName}>
										{fieldName}
									</option>
								))}
							</select>
						)}
					</div>
				</div>
				<div className="array-table-container">
					<table>
						<thead>
							<tr>
								{arrayVar.properties.map((prop) => (
									<th key={prop}>{prop}</th>
								))}
								<th className="actions-column">Actions</th>
							</tr>
						</thead>
						<tbody>
							{arrayVar.rows.map((row, rowIndex) => (
								<tr key={rowIndex}>
									{arrayVar.properties.map((prop) => (
										<td key={prop}>
											{arrayVar.booleanProperties.includes(prop) ? (
												<label className="list-boolean-cell">
													<input
														type="checkbox"
														checked={Boolean(row[prop])}
														onChange={(e) => handleArrayRowChange(arrayVar.name, rowIndex, prop, e.target.checked)}
													/>
												</label>
											) : (
												<input
													type="text"
													value={String(row[prop] ?? "")}
													onChange={(e) => handleArrayRowChange(arrayVar.name, rowIndex, prop, e.target.value)}
													placeholder={`Value for ${prop}`}
												/>
											)}
										</td>
									))}
									<td className="actions-column">
										<button
											className="remove-row-btn"
											onClick={() => handleRemoveRow(arrayVar.name, rowIndex)}
											disabled={arrayVar.rows.length === 1}
											title={arrayVar.rows.length === 1 ? "Cannot remove last row" : "Remove row"}
										>
											<Icon icon="delete-close-x" size={14} />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		)
	}

	// Handler for conditional variable changes - updated to handle text input
	const handleConditionalChange = (conditional: string, value: string) => {
		// If input is empty, set to boolean false
		// Otherwise use the entered value
		const newValue = value.trim() === "" ? false : value
		const updatedVariables = setNestedValue({...variablesObject}, conditional, newValue)
		setVariablesObject(updatedVariables)
	}

	return (
		<div className="mustache-editor">
			<div className="mustache-editor-controls">
				<div className="view-switcher">
					<button
						className={classNames("view-btn", viewMode === "edit-only" && "active")}
						onClick={() => setViewMode("edit-only")}
						title="Edit Only"
					>
						<Icon icon="pencil-1--interface-essential" size={16} />
						<span>Edit</span>
					</button>
					<button
						className={classNames("view-btn", viewMode === "side-by-side" && "active")}
						onClick={() => setViewMode("side-by-side")}
						title="Side by Side"
					>
						<Icon icon="module-three--interface-essential" size={16} />
						<span>Split</span>
					</button>
					<button
						className={classNames("view-btn", viewMode === "preview-only" && "active")}
						onClick={() => setViewMode("preview-only")}
						title="Preview Only"
					>
						<Icon icon="document-paragraph-text-image-1--interface-essential" size={16} />
						<span>Preview</span>
					</button>
				</div>

				<div className="center-controls">
					{showSubjectField && (
						<div className="subject-field">
							<label htmlFor="template-subject">Subject:</label>
							<input
								type="text"
								id="template-subject"
								value={subject}
								onChange={(e) => setSubject(e.target.value)}
								placeholder="Email subject"
								style={{width: "300%", maxWidth: "600px"}}
							/>
						</div>
					)}
				</div>

				<Button
					onClick={handleSave}
					theme="success"
					disabled={hasScriptTags}
					title={hasScriptTags ? "Cannot save templates with script tags" : "Save template"}
				>
					Save New Template
				</Button>
			</div>

			{hasScriptTags && (
				<div className="security-warning">
					<Icon icon="alert-circle--interface-essential" size={16} />
					<span>{SECURITY_WARNING}</span>
				</div>
			)}

			<div className={classNames("mustache-editor-layout", viewMode)}>
				{viewMode !== "preview-only" && (
					<div className="mustache-editor-edit-panel">
						<div className="mustache-editor-panel html-editor">
							<div className="panel-title">
								<span>HTML Template</span>
							</div>
							<div className="editor-container">
								<Editor
									height="calc(100% - 10px)"
									language="html"
									value={htmlContent}
									onChange={handleHtmlChange}
									options={{
										minimap: {enabled: false},
										lineNumbers: "on",
										scrollBeyondLastLine: false,
										wordWrap: "on",
									}}
								/>
							</div>
						</div>

						<div className="mustache-editor-panel variables-editor">
							<div className="panel-title-with-action">
								<span>Template Variables</span>
								<div className="panel-actions">
									<button
										className="toggle-json-btn"
										onClick={toggleRawJson}
										title={showRawJson ? "Show Form Fields" : "Show Raw JSON"}
									>
										{showRawJson ? "Form View" : "JSON View"}
									</button>
								</div>
							</div>

							<div className="editor-container">
								{showRawJson ? (
									<Editor
										height="calc(100% - 10px)"
										language="json"
										value={variablesJson}
										onChange={handleRawJsonChange}
										options={{
											minimap: {enabled: false},
											lineNumbers: "on",
											scrollBeyondLastLine: false,
											wordWrap: "on",
										}}
									/>
								) : (
									<div className="variables-form">
										{/* Array Variables Tables */}
										{arrayVariables.map((arrayVar) => renderArrayTable(arrayVar))}

										{/* Conditional Variables - Simple UI */}
										{conditionalVariables.length > 0 && (
											<div>
												<div style={{marginBottom: "10px", borderBottom: "1px solid #ddd", paddingBottom: "5px"}}>
													<strong>Conditional Sections</strong>
												</div>
												{conditionalVariables.map((conditional) => {
													const targetLists = arrayVariables.filter(
														(arrayVar) =>
															!arrayAsBooleanMap[arrayVar.name] && !arrayVar.properties.includes(conditional)
													)

													return (
														<div key={conditional} className="variable-field" style={{marginBottom: "8px"}}>
															<label title={`Section: {{#${conditional}}}`}>{conditional}</label>
															<input
																type="text"
																value={String(getNestedValue(variablesObject, conditional) || "")}
																onChange={(e) => handleConditionalChange(conditional, e.target.value)}
																placeholder={`Value for conditional ${conditional}`}
															/>
															{targetLists.length > 0 && (
																<select
																	className="add-list-field-select"
																	value=""
																	onChange={(e) => {
																		if (e.target.value) handleMoveFieldToList(conditional, e.target.value)
																	}}
																>
																	<option value="">Move into list…</option>
																	{targetLists.map((arrayVar) => (
																		<option key={arrayVar.name} value={arrayVar.name}>
																			{arrayVar.name}
																		</option>
																	))}
																</select>
															)}
														</div>
													)
												})}
											</div>
										)}

										{/* Regular Variables */}
										{Object.entries(flattenedVariables).length > 0 ? (
											Object.entries(flattenedVariables).map(([variable, value]) => (
												<div key={variable} className="variable-field">
													<label title={variable}>{variable}</label>
													<input
														type="text"
														value={String(value)}
														onChange={(e) => handleVariableChange(variable, e.target.value)}
														placeholder={`Value for ${variable}`}
													/>
												</div>
											))
										) : (
											<div className="no-variables-message">
												No variables detected in template.
												<br />
												Add variables using double curly braces: <code>{"{{variable}}"}</code>
											</div>
										)}
									</div>
								)}
								{previewError && <div className="error-message">{previewError}</div>}
							</div>
						</div>
					</div>
				)}

				{viewMode !== "edit-only" && (
					<div className="mustache-editor-preview-panel">
						<div className="mustache-editor-preview">
							<h3 className="panel-title">Live Preview</h3>
							{previewError ? (
								<div className="error-message">{previewError}</div>
							) : (
								<div className="preview-container">
									<iframe
										key={`preview-${renderedHtml.length}`}
										title="Mustache Preview"
										srcDoc={
											renderedHtml ||
											`<div style="padding: 20px; color: ${UI_COLORS.neutral};">Preparing preview...</div>`
										}
										className="preview-iframe"
										sandbox="allow-scripts"
									/>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
